import { useCallback, useState } from 'react'
import { useToast } from '../contexts/ToastContext'
import { useAuth } from '../contexts/AuthContext'
import { redirectToCheckout, startCheckout } from '../services/checkoutService'
import { clearPendingUnlockScanId, PENDING_UNLOCK_KEY } from './useReportUnlock'
import { clearSelectedPlan, storeSelectedPlan } from '../utils/selectedPlan'
import type { CheckoutPlanId } from '../data/pricingPlans'

interface CheckoutOptions {
  /** The scan being unlocked — required for single_scan. */
  scanId?: string | null
}

interface UseCheckoutReturn {
  /** Starts a server-priced checkout for the plan and follows the redirect. */
  onCheckout: (plan: CheckoutPlanId, options?: CheckoutOptions) => Promise<void>
  pendingPlan: CheckoutPlanId | null
  /** Set when an unauthenticated visitor starts checkout. */
  authPromptPlan: CheckoutPlanId | null
  dismissAuthPrompt: () => void
}

/**
 * The single frontend checkout abstraction used by every paid CTA.
 * It sends only { plan, scan_id, location, page } to `start-checkout`;
 * the server decides amount, currency, email and metadata.
 *
 * Deliberately router-free: the pricing section also renders on the
 * statically served marketing entry points.
 */
export function useCheckout(
  location = 'pricing_section',
  defaults: CheckoutOptions = {},
): UseCheckoutReturn {
  const { user } = useAuth()
  // Marketing entry points render without the toast provider.
  let toast: { showToast: ReturnType<typeof useToast>['showToast'] } | null =
    null
  try {
    toast = useToast()
  } catch {
    toast = null
  }
  const [pendingPlan, setPendingPlan] = useState<CheckoutPlanId | null>(null)
  const [authPromptPlan, setAuthPromptPlan] = useState<CheckoutPlanId | null>(
    null,
  )
  const defaultScanId = defaults.scanId ?? null

  const notify = useCallback(
    (message: string, type: 'error' | 'info' = 'error') => {
      if (toast) toast.showToast(message, type, 5000)
      else window.alert(message)
    },
    [toast],
  )

  const onCheckout = useCallback(
    async (plan: CheckoutPlanId, options?: CheckoutOptions) => {
      // One checkout at a time — guards against duplicate requests.
      if (pendingPlan) return
      if (!user) {
        // Remember the selection so checkout resumes after authentication.
        storeSelectedPlan(plan)
        setAuthPromptPlan(plan)
        return
      }
      const scanId = plan === 'single_scan' ? options?.scanId ?? defaultScanId : null
      setPendingPlan(plan)
      const result = await startCheckout({ plan, scanId, location })
      if (!result.ok) {
        console.error(`Checkout failed (${location}):`, result.code)
        notify(result.message, result.code === 'already_pro' || result.code === 'already_unlocked' ? 'info' : 'error')
        setPendingPlan(null)
        return
      }
      clearSelectedPlan()
      // Routing hand-off only — never grants access.
      if (plan === 'single_scan' && scanId) {
        try {
          localStorage.setItem(
            PENDING_UNLOCK_KEY,
            JSON.stringify({ scanId, startedAt: Date.now() }),
          )
        } catch {
          /* the return URL also carries scan_id */
        }
      } else {
        clearPendingUnlockScanId()
      }
      redirectToCheckout(result.authorizationUrl)
    },
    [defaultScanId, location, notify, pendingPlan, user],
  )
  const dismissAuthPrompt = useCallback(() => setAuthPromptPlan(null), [])
  return {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt,
  }
}
